import React, { useEffect, useState } from 'react';
import gsap from 'gsap';

import styled, { css } from 'styled-components';
import { fontFamilies } from '~/theme/typography';

interface BadgeProps {
  count: number;
  children: React.ReactNode;
  $backgroundColor?: string;
  $transformCount?: number;
}

export const Badge: React.FC<BadgeProps> = ({
  count,
  children,
  $backgroundColor,
  $transformCount = 1,
}) => {
  const [prevCount, setPrevCount] = useState(count);

  useEffect(() => {
    if (count !== prevCount) {
      playAppearAnimation();
      setPrevCount(count);
    }
  }, [count, prevCount]);

  const playAppearAnimation = () => {
    gsap.to('.badge-count', {
      scale: 0.5,
      duration: 0.25,
      onComplete: () => {
        gsap.to('.badge-count', {
          scale: 1,
          duration: 0.25,
        });
      },
    });
  };

  return (
    <BadgeContainer>
      <BadgeCount className="badge-count" $backgroundColor={$backgroundColor}>
        <BadgeCountText $transformCount={$transformCount}>
          {count}
        </BadgeCountText>
      </BadgeCount>
      {children}
    </BadgeContainer>
  );
};

export const BadgeContainer = styled.div`
  position: relative;
  display: flex;
  margin-right: 1rem;
  height: 30px;
  align-items: center;

  @media ${({ theme }) => theme.breakpoints.lg} {
    margin-right: 0.5rem;
  }
`;

export const BadgeCount = styled.span<{ $backgroundColor?: string }>`
  position: absolute;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;

  background-color: ${({ theme, $backgroundColor }) =>
    $backgroundColor ? $backgroundColor : theme.colors.primaryDark};

  right: -1rem;
  top: -0.5rem;

  @media ${({ theme }) => theme.breakpoints.lg} {
    width: 1rem;
    height: 1rem;
    right: -8px;
    top: -4px;
  }
`;

export const BadgeCountText = styled.span<{ $transformCount?: number }>`
  font-size: 0.5rem;
  text-align: center;
  font-weight: 100;
  letter-spacing: 0.15px;
  font-family: ${fontFamilies.PantonRustHeavy};

  color: ${({ theme }) => theme.colors.white};

  ${({ theme, $transformCount }) =>
    $transformCount
      ? css`
          @media ${theme.breakpoints.lg} {
            transform: translateY(${$transformCount}px);
          }
        `
      : ''}
`;
